import React, { useCallback } from "react";
import { ScrollView } from "react-native";
import { useI18n } from "../../hooks/i18n-hooks";
import { useMutation } from "@apollo/client";
import { UPDATE_WORKER_PROFILE } from "../../graphql/core";
import { AuthManagerContext, AuthStatus } from "../../contexts/AuthManager";
import TextField from "../../components/TextField";
import VStack from "../../ui_atoms/VStack";
import { isIbanValid } from "../../iban";
import ScreenCard from "../ScreenCard";
import { useApolloErrorSnackbar } from "../../hooks/snackbar";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useCloseEditor } from "../profile-editor/use-close-editor";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useChainInputFocus } from "../../hooks/use-chain-input-focus";

type FormData = {
    bankName: string;
    iban: string;
};

type BankAccountEditorScreenProps = StackScreenProps<RootStackParamList, "BankAccountEdit">;

const BankAccountEditorScreen = ({ navigation, route }: BankAccountEditorScreenProps) => {
    const { profileId } = (route.params || {}) as RootStackParamList["BankAccountEditModal"];
    const { user, status, refreshUser } = React.useContext(AuthManagerContext);
    const [updateWorkerProfile, { error: saveError, loading: saving, reset }] = useMutation(
        UPDATE_WORKER_PROFILE,
        profileId ? { context: { liftProfile: profileId } } : undefined,
    );

    useApolloErrorSnackbar(saveError, reset);

    const loading = status == AuthStatus.Loading;
    const i18n = useI18n();
    const workerProfile = user?.workerProfile;

    const onSubmit = useCallback<SubmitHandler<FormData>>(
        async (data) => {
            try {
                await updateWorkerProfile({
                    variables: {
                        changes: {
                            bankAccount: {
                                id: workerProfile?.bankAccount?.id || null,
                                name: data.bankName,
                                iban: data.iban,
                            },
                        },
                    },
                });
                navigation.goBack();
                void refreshUser?.(); // do not await to avoid visual glitch
            } catch (ex) {
                // console.error(ex);
                // this is handled by saveError in useMutation
            }
        },
        [updateWorkerProfile, workerProfile?.bankAccount?.id, navigation, refreshUser],
    );

    const {
        handleSubmit,
        control,
        formState: { isValid, isDirty },
    } = useForm<FormData>({
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: {
            bankName: workerProfile?.bankAccount?.name || "",
            iban: workerProfile?.bankAccount?.iban || "",
        },
    });

    const { chainInputFocus } = useChainInputFocus();

    const disabled = loading || saving;

    useCloseEditor(isDirty && !saving);

    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.t("save"),
                    onPress: handleSubmit(onSubmit),
                    validating: saving,
                    isValid: isValid && isDirty,
                    mode: "secondary",
                },
            ]}
        >
            <ScrollView>
                <VStack v={1} gap={0.5} alignment="stretch" h="2/3">
                    <Controller
                        control={control}
                        name="bankName"
                        rules={{
                            required: true,
                            validate: (value) => {
                                if (!value) {
                                    return i18n.t("worker_profile.errors.iban_bank_name");
                                }
                                return true;
                            },
                        }}
                        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                            <TextField
                                onChangeText={onChange}
                                onBlur={onBlur}
                                error={error?.message}
                                value={value}
                                label={i18n.t("worker_profile.bank_name")}
                                disabled={disabled}
                                {...chainInputFocus(0)}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="iban"
                        rules={{
                            required: true,
                            validate: (value) => {
                                if (!isIbanValid(value)) {
                                    return i18n.t("worker_profile.errors.iban_number");
                                }
                                return true;
                            },
                        }}
                        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                            <TextField
                                autoCapitalize="characters"
                                error={error?.message}
                                value={value}
                                label={i18n.t("worker_profile.bank_iban")}
                                onChangeText={onChange}
                                onBlur={onBlur}
                                disabled={disabled}
                                {...chainInputFocus(1)}
                            />
                        )}
                    />
                </VStack>
            </ScrollView>
        </ScreenCard>
    );
};

export default BankAccountEditorScreen;
